import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel,
  DropdownMenuSeparator, DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import {
  LayoutDashboard, Users, Network, UserCog, Settings, LogOut, Map, FileText,
  ShoppingCart, Menu, X, TicketIcon, BarChart3, Calendar, FileSignature,
  MessageCircle, ChevronLeft, ChevronRight, ChevronDown, IndianRupee, Globe,
  Tv, Monitor, Briefcase, Store, ListTodo, FolderOpen, Train, KeyRound,
  Fingerprint, PanelLeft, PanelTop, Search, Layers, Wrench, GitBranch, ClipboardList, PackageCheck
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const ALL_ROLES = ['admin', 'onboarding', 'sales', 'accounts', 'delivery', 'support', 'noc', 'manager'];

// ----- Grouped navigation definition -----
const buildNavGroups = (whatsappUnread) => [
  {
    id: 'crm', label: 'CRM', icon: Users,
    items: [
      { path: '/customers', label: 'Customers', icon: Users, roles: [...ALL_ROLES, 'enterprise_sales', 'lco_sales'] },
      { path: '/customers?type=partner', label: 'Master Distributors', icon: Briefcase, roles: ['admin', 'onboarding', 'sales', 'accounts', 'support', 'noc', 'manager'] },
      { path: '/customers?type=iptv_distributor', label: 'Sub Distributors', icon: Briefcase, roles: ['admin', 'onboarding', 'sales', 'accounts', 'support', 'noc', 'manager'] },
      { path: '/customers?type=lco', label: 'LCO', icon: Store, roles: ['admin', 'onboarding', 'sales', 'accounts', 'support', 'noc', 'manager', 'lco_sales'] },
      { path: '/hierarchy', label: 'Distribution Tree', icon: GitBranch, roles: ['admin', 'accounts', 'manager'] },
      { path: '/circuits', label: 'Circuits', icon: Network, roles: ALL_ROLES },
      { path: '/map', label: 'Map View', icon: Map, roles: ALL_ROLES },
    ]
  },
  {
    id: 'operations', label: 'Operations', icon: Wrench,
    items: [
      { path: '/tickets', label: 'Tickets', icon: TicketIcon, roles: ALL_ROLES },
      { path: '/tasks', label: 'Tasks', icon: ListTodo, roles: [...ALL_ROLES, 'manager', 'hr', 'enterprise_sales', 'lco_sales'] },
      { path: '/shift-roster', label: 'Shift Roster', icon: Calendar, roles: ['admin', 'manager', 'support', 'noc'] },
      { path: '/whatsapp', label: 'WhatsApp', icon: MessageCircle, roles: ['admin', 'onboarding', 'accounts', 'delivery', 'support', 'noc', 'manager'], badge: whatsappUnread },
      { path: '/portal-otp-inbox', label: 'Portal OTP Inbox', icon: KeyRound, roles: ['admin', 'support'] },
      { path: '/files', label: 'Files', icon: FolderOpen, roles: ['admin', 'accounts', 'onboarding', 'support', 'noc', 'manager'] },
    ]
  },
  {
    id: 'finance', label: 'Finance', icon: IndianRupee,
    items: [
      { path: '/payments', label: 'Payments', icon: IndianRupee, roles: ['admin', 'accounts', 'sales', 'collection_manager', 'manager'] },
      { path: '/accounting', label: 'Accounting', icon: BarChart3, roles: ['admin', 'accounts'] },
      { path: '/quotations', label: 'Quotations', icon: FileText, roles: ['admin', 'onboarding', 'sales', 'accounts', 'noc', 'manager', 'enterprise_sales', 'lco_sales'] },
      { path: '/sales-orders', label: 'Sales Orders (ILL/MPLS)', icon: ClipboardList, roles: ['admin', 'manager', 'sales', 'enterprise_sales', 'accounts', 'onboarding', 'delivery', 'noc'] },
      { path: '/lco-packages', label: 'LCO Packages', icon: PackageCheck, roles: ['admin', 'manager', 'onboarding', 'lco_sales'] },
      { path: '/purchase-orders', label: 'Purchase Orders', icon: ShoppingCart, roles: ['admin', 'accounts', 'delivery', 'sales'] },
      { path: '/agreements', label: 'Agreements', icon: FileSignature, roles: ['admin', 'onboarding'] },
    ]
  },
  {
    id: 'services', label: 'Services', icon: Layers,
    items: [
      { path: '/ott', label: 'OTT Subscriptions', icon: Tv, roles: ['admin', 'accounts', 'onboarding', 'support', 'noc'] },
      { path: '/iptv', label: 'IPTV Packages', icon: Monitor, roles: ['admin', 'accounts', 'onboarding', 'support', 'noc'] },
      { path: '/dns', label: 'DNS Management', icon: Globe, roles: ['admin', 'noc'] },
      { path: '/ipam', label: 'IPAM', icon: Network, roles: ['admin', 'noc', 'support', 'delivery'] },
      { path: '/railtel', label: 'ANT+RAILTEL', icon: Train, roles: ['admin', 'accounts', 'manager', 'support'] },
      { path: '/partner-api', label: 'Partner API', icon: KeyRound, roles: ['admin', 'accounts'] },
    ]
  },
  {
    id: 'admin', label: 'Admin', icon: Settings,
    items: [
      { path: '/employees', label: 'Users', icon: UserCog, roles: ['admin'] },
      { path: '/hr', label: 'HR & Attendance', icon: Fingerprint, roles: ['admin', 'manager', 'hr'] },
      { path: '/tickets/analytics', label: 'Analytics', icon: BarChart3, roles: ['admin'] },
      { path: '/settings', label: 'Settings', icon: Settings, roles: ['admin'] },
    ]
  },
];

const DASHBOARD_ITEM = { path: '/', label: 'Dashboard', icon: LayoutDashboard, roles: [...ALL_ROLES, 'enterprise_sales', 'lco_sales'] };

// ----- Active route matching (path + ?type= aware) -----
const isItemActive = (item, location) => {
  const [itemPath, itemQuery = ''] = item.path.split('?');
  const itemTypeMatch = /(?:^|&)type=([^&]*)/.exec(itemQuery);
  const itemType = itemTypeMatch ? itemTypeMatch[1] : null;
  const currentType = new URLSearchParams(location.search).get('type');
  if (itemType !== null) return location.pathname === itemPath && currentType === itemType;
  if (itemPath === '/customers') return location.pathname === '/customers' && !currentType;
  if (itemPath === '/') return location.pathname === '/';
  if (itemPath === '/tickets') return (location.pathname === '/tickets' || (location.pathname.startsWith('/tickets/') && location.pathname !== '/tickets/analytics'));
  return location.pathname === itemPath || location.pathname.startsWith(itemPath + '/');
};

const getRoleBadgeColor = (role) => {
  const colors = {
    admin: 'bg-red-500 text-white', manager: 'bg-rose-500 text-white',
    onboarding: 'bg-sky-500 text-white', sales: 'bg-indigo-500 text-white',
    accounts: 'bg-emerald-500 text-white', delivery: 'bg-violet-500 text-white',
    support: 'bg-amber-500 text-white', noc: 'bg-cyan-500 text-white',
    enterprise_sales: 'bg-blue-600 text-white', lco_sales: 'bg-orange-500 text-white'
  };
  return colors[role] || 'bg-slate-500 text-white';
};

export default function Layout({ children }) {
  const { user, logout, token, updateUser } = useAuth();
  const location = useLocation();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [whatsappUnread, setWhatsappUnread] = useState(0);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [sidebarHovered, setSidebarHovered] = useState(false);
  const [openGroups, setOpenGroups] = useState(() => {
    try { return JSON.parse(localStorage.getItem('nav_groups_open')) || {}; } catch { return {}; }
  });
  const [mobileSearch, setMobileSearch] = useState('');
  const [navMode, setNavMode] = useState(() => localStorage.getItem('nav_layout') || 'sidebar');

  const isWhatsAppPage = location.pathname === '/whatsapp';

  // Adopt server-side preference if no local choice yet
  useEffect(() => {
    if (!localStorage.getItem('nav_layout') && user?.nav_layout) {
      setNavMode(user.nav_layout);
    }
  }, [user?.nav_layout]);

  const changeNavMode = useCallback(async (mode) => {
    setNavMode(mode);
    localStorage.setItem('nav_layout', mode);
    try {
      await fetch(`${API}/api/users/me/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ nav_layout: mode })
      });
      if (user) updateUser({ ...user, nav_layout: mode });
    } catch { /* saved locally anyway */ }
  }, [token, user, updateUser]);

  useEffect(() => {
    if (isWhatsAppPage) setSidebarCollapsed(true);
  }, [isWhatsAppPage]);

  useEffect(() => { setIsMobileMenuOpen(false); setMobileSearch(''); }, [location.pathname, location.search]);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    const fetchUnread = async () => {
      if (!token) return;
      try {
        const response = await fetch(`${API}/api/whatsapp/conversations`, {
          headers: { 'Authorization': `Bearer ${token}` }
        });
        if (response.ok) {
          const data = await response.json();
          setWhatsappUnread(data.total_unread || 0);
        }
      } catch { /* silent */ }
    };
    fetchUnread();
    const interval = setInterval(fetchUnread, 30000);
    return () => clearInterval(interval);
  }, [token]);

  const userRoles = useMemo(() => {
    if (user?.roles && user.roles.length > 0) return user.roles;
    return user?.role ? [user.role] : [];
  }, [user]);

  // Filter groups & items by role
  const visibleGroups = useMemo(() => {
    return buildNavGroups(whatsappUnread)
      .map(g => ({ ...g, items: g.items.filter(item => userRoles.some(r => item.roles.includes(r))) }))
      .filter(g => g.items.length > 0);
  }, [userRoles, whatsappUnread]);

  const showDashboard = userRoles.some(r => DASHBOARD_ITEM.roles.includes(r));

  // Flat list (dashboard first) for bottom nav / search
  const flatItems = useMemo(() => {
    const items = showDashboard ? [DASHBOARD_ITEM] : [];
    visibleGroups.forEach(g => items.push(...g.items));
    return items;
  }, [visibleGroups, showDashboard]);

  const activeGroupId = useMemo(() => {
    const g = visibleGroups.find(g => g.items.some(item => isItemActive(item, location)));
    return g?.id || null;
  }, [visibleGroups, location]);

  const isGroupOpen = (groupId) => {
    if (groupId in openGroups) return openGroups[groupId];
    return groupId === activeGroupId; // auto-open active group by default
  };

  const toggleGroup = (groupId) => {
    const next = { ...openGroups, [groupId]: !isGroupOpen(groupId) };
    setOpenGroups(next);
    localStorage.setItem('nav_groups_open', JSON.stringify(next));
  };

  const groupBadge = (group) => group.items.reduce((sum, i) => sum + (i.badge || 0), 0);

  const showExpanded = !sidebarCollapsed || sidebarHovered;

  // ----- Shared nav link -----
  const NavLink = ({ item, compact = false, onNavigate }) => {
    const Icon = item.icon;
    const active = isItemActive(item, location);
    return (
      <Link
        to={item.path}
        onClick={onNavigate}
        className={`flex items-center px-3 py-2.5 rounded-md transition-colors text-sm ${
          active
            ? 'bg-red-600/20 text-white border-l-4 border-red-500'
            : 'hover:bg-slate-800 text-slate-300 hover:text-white active:bg-slate-700 border-l-4 border-transparent'
        } ${compact ? 'py-2' : ''}`}
        data-testid={`nav-link-${item.label.toLowerCase().replace(/ /g, '-')}`}
      >
        <Icon className="w-[18px] h-[18px] mr-3 flex-shrink-0" />
        <span className="font-medium flex-1 truncate">{item.label}</span>
        {item.badge > 0 && (
          <Badge className="bg-green-500 text-white text-xs h-5 min-w-[20px] flex items-center justify-center ml-2">
            {item.badge > 99 ? '99+' : item.badge}
          </Badge>
        )}
      </Link>
    );
  };

  // ----- Layout mode switcher (segmented) -----
  const ModeSwitcher = ({ dark = true }) => (
    <div className={`flex items-center rounded-lg p-0.5 ${dark ? 'bg-slate-800' : 'bg-slate-200'}`} data-testid="nav-mode-switcher">
      <TooltipProvider delayDuration={200}>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => changeNavMode('sidebar')}
              className={`p-1.5 rounded-md transition-colors ${navMode === 'sidebar' ? 'bg-red-600 text-white' : dark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="nav-mode-sidebar-btn"
              aria-label="Sidebar layout"
            >
              <PanelLeft className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-slate-800 text-white border-slate-700"><p>Left Sidebar</p></TooltipContent>
        </Tooltip>
        <Tooltip>
          <TooltipTrigger asChild>
            <button
              onClick={() => changeNavMode('topbar')}
              className={`p-1.5 rounded-md transition-colors ${navMode === 'topbar' ? 'bg-red-600 text-white' : dark ? 'text-slate-400 hover:text-white' : 'text-slate-500 hover:text-slate-900'}`}
              data-testid="nav-mode-topbar-btn"
              aria-label="Top menu layout"
            >
              <PanelTop className="w-4 h-4" />
            </button>
          </TooltipTrigger>
          <TooltipContent side="top" className="bg-slate-800 text-white border-slate-700"><p>Top Menu Bar</p></TooltipContent>
        </Tooltip>
      </TooltipProvider>
    </div>
  );

  // ----- Grouped nav (used by expanded sidebar + mobile drawer) -----
  const GroupedNav = ({ onNavigate, searchTerm = '' }) => {
    const term = searchTerm.trim().toLowerCase();
    if (term) {
      const matches = flatItems.filter(i => i.label.toLowerCase().includes(term));
      return (
        <div className="space-y-0.5">
          {matches.length === 0 && <p className="text-xs text-slate-500 px-3 py-2">No menu found</p>}
          {matches.map(item => <NavLink key={item.path} item={item} onNavigate={onNavigate} />)}
        </div>
      );
    }
    return (
      <div className="space-y-1">
        {showDashboard && <NavLink item={DASHBOARD_ITEM} onNavigate={onNavigate} />}
        {visibleGroups.map(group => {
          const GroupIcon = group.icon;
          const open = isGroupOpen(group.id);
          const badge = groupBadge(group);
          const isActiveGroup = group.id === activeGroupId;
          return (
            <div key={group.id}>
              <button
                onClick={() => toggleGroup(group.id)}
                className={`w-full flex items-center px-3 py-2.5 rounded-md transition-colors text-xs font-semibold uppercase tracking-wider ${
                  isActiveGroup ? 'text-red-400' : 'text-slate-500 hover:text-slate-300'
                }`}
                data-testid={`nav-group-${group.id}`}
              >
                <GroupIcon className="w-4 h-4 mr-3 flex-shrink-0" />
                <span className="flex-1 text-left">{group.label}</span>
                {badge > 0 && !open && (
                  <span className="w-2 h-2 bg-green-500 rounded-full mr-2"></span>
                )}
                <ChevronDown className={`w-4 h-4 transition-transform duration-200 ${open ? 'rotate-180' : ''}`} />
              </button>
              <div className={`overflow-hidden transition-all duration-200 ${open ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'}`}>
                <div className="pl-2 space-y-0.5 pb-1">
                  {group.items.map(item => <NavLink key={item.path} item={item} compact onNavigate={onNavigate} />)}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  // ----- Collapsed sidebar (icon rail with flyout popups) -----
  const CollapsedRail = () => (
    <nav className="p-2 space-y-1 flex-1 overflow-y-auto">
      <TooltipProvider delayDuration={0}>
        {showDashboard && (
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="relative">
                <Link
                  to="/"
                  className={`flex items-center justify-center px-2 py-3 rounded-md transition-colors ${
                    location.pathname === '/' ? 'bg-red-600/20 text-white' : 'hover:bg-slate-800 text-slate-300 hover:text-white'
                  }`}
                  data-testid="nav-link-dashboard"
                >
                  <LayoutDashboard className="w-5 h-5" />
                </Link>
              </div>
            </TooltipTrigger>
            <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700"><p>Dashboard</p></TooltipContent>
          </Tooltip>
        )}
        {visibleGroups.map(group => {
          const GroupIcon = group.icon;
          const badge = groupBadge(group);
          return (
            <Tooltip key={group.id}>
              <TooltipTrigger asChild>
                <div className={`relative flex items-center justify-center px-2 py-3 rounded-md cursor-pointer transition-colors ${
                  group.id === activeGroupId ? 'bg-red-600/20 text-white' : 'hover:bg-slate-800 text-slate-400 hover:text-white'
                }`}>
                  <GroupIcon className="w-5 h-5" />
                  {badge > 0 && <span className="absolute top-1 right-1 w-2 h-2 bg-green-500 rounded-full"></span>}
                </div>
              </TooltipTrigger>
              <TooltipContent side="right" className="bg-slate-800 text-white border-slate-700 p-2">
                <p className="font-semibold text-xs uppercase tracking-wider text-slate-400 mb-1">{group.label}</p>
                {group.items.map(i => <p key={i.path} className="text-xs py-0.5">{i.label}</p>)}
              </TooltipContent>
            </Tooltip>
          );
        })}
      </TooltipProvider>
    </nav>
  );

  // ----- Sidebar user footer -----
  const SidebarFooter = ({ collapsed }) => (
    <div className={`${collapsed ? 'p-2' : 'p-4'} border-t border-slate-800 mt-auto`}>
      {!collapsed && (
        <>
          <div className="mb-3 px-1">
            <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            <div className="mt-2 flex flex-wrap gap-1">
              {userRoles.map(role => (
                <Badge key={role} className={`text-xs ${getRoleBadgeColor(role)}`}>{role?.toUpperCase()}</Badge>
              ))}
            </div>
          </div>
          <div className="mb-3 flex items-center justify-between px-1">
            <span className="text-xs text-slate-500">Menu layout</span>
            <ModeSwitcher />
          </div>
        </>
      )}
      <Button
        onClick={logout}
        variant="outline"
        className={`w-full ${collapsed ? 'p-2' : ''} border-slate-700 text-slate-300 hover:bg-red-600 hover:text-white hover:border-red-600 active:bg-red-700`}
        data-testid="sidebar-logout-button"
      >
        <LogOut className={`w-4 h-4 ${collapsed ? '' : 'mr-2'}`} />
        {!collapsed && 'Logout'}
      </Button>
    </div>
  );

  // ----- Top bar (desktop topbar mode) -----
  const TopBar = () => (
    <header className="hidden lg:flex fixed top-0 left-0 right-0 z-30 bg-slate-900 text-slate-300 h-16 items-center px-4 shadow-lg gap-1" data-testid="topbar">
      <Link to="/" className="flex items-center gap-2 mr-4 flex-shrink-0">
        <img src="/logo.png" alt="ANT Broadband" className="h-10 w-auto object-contain" />
      </Link>

      {showDashboard && (
        <Link
          to="/"
          className={`flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
            location.pathname === '/' ? 'bg-red-600/20 text-white' : 'hover:bg-slate-800 hover:text-white'
          }`}
          data-testid="topbar-dashboard-link"
        >
          <LayoutDashboard className="w-4 h-4" />
          Dashboard
        </Link>
      )}

      {visibleGroups.map(group => {
        const GroupIcon = group.icon;
        const badge = groupBadge(group);
        const isActiveGroup = group.id === activeGroupId;
        return (
          <DropdownMenu key={group.id}>
            <DropdownMenuTrigger asChild>
              <button
                className={`relative flex items-center gap-2 px-3 py-2 rounded-md text-sm font-medium transition-colors outline-none ${
                  isActiveGroup ? 'bg-red-600/20 text-white' : 'hover:bg-slate-800 hover:text-white'
                }`}
                data-testid={`topbar-group-${group.id}`}
              >
                <GroupIcon className="w-4 h-4" />
                {group.label}
                {badge > 0 && <span className="absolute top-1.5 right-1 w-2 h-2 bg-green-500 rounded-full"></span>}
                <ChevronDown className="w-3.5 h-3.5 opacity-60" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start" className="w-60 bg-slate-900 border-slate-700 text-slate-300">
              {group.items.map(item => {
                const Icon = item.icon;
                const active = isItemActive(item, location);
                return (
                  <DropdownMenuItem key={item.path} asChild className={`cursor-pointer focus:bg-slate-800 focus:text-white ${active ? 'bg-red-600/20 text-white' : ''}`}>
                    <Link to={item.path} data-testid={`topbar-link-${item.label.toLowerCase().replace(/ /g, '-')}`} className="flex items-center w-full">
                      <Icon className="w-4 h-4 mr-2.5" />
                      <span className="flex-1">{item.label}</span>
                      {item.badge > 0 && (
                        <Badge className="bg-green-500 text-white text-xs h-5 min-w-[20px] flex items-center justify-center ml-2">
                          {item.badge > 99 ? '99+' : item.badge}
                        </Badge>
                      )}
                    </Link>
                  </DropdownMenuItem>
                );
              })}
            </DropdownMenuContent>
          </DropdownMenu>
        );
      })}

      <div className="flex-1" />

      <ModeSwitcher />

      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button className="flex items-center gap-2 ml-2 px-2 py-1.5 rounded-md hover:bg-slate-800 transition-colors outline-none" data-testid="topbar-user-menu">
            <div className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center text-sm font-semibold flex-shrink-0">
              {(user?.full_name || 'U').charAt(0).toUpperCase()}
            </div>
            <div className="text-left hidden xl:block max-w-[140px]">
              <p className="text-xs font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-[10px] text-slate-400 truncate">{userRoles.join(', ').toUpperCase()}</p>
            </div>
            <ChevronDown className="w-3.5 h-3.5 opacity-60" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="w-56 bg-slate-900 border-slate-700 text-slate-300">
          <DropdownMenuLabel>
            <p className="text-sm text-white truncate">{user?.full_name}</p>
            <p className="text-xs text-slate-400 font-normal truncate">{user?.email}</p>
            <div className="mt-1.5 flex flex-wrap gap-1">
              {userRoles.map(role => (
                <Badge key={role} className={`text-[10px] ${getRoleBadgeColor(role)}`}>{role?.toUpperCase()}</Badge>
              ))}
            </div>
          </DropdownMenuLabel>
          <DropdownMenuSeparator className="bg-slate-700" />
          <DropdownMenuItem onClick={logout} className="cursor-pointer text-red-400 focus:bg-red-600 focus:text-white" data-testid="topbar-logout-button">
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </header>
  );

  // ----- Sidebar (desktop sidebar mode) -----
  const Sidebar = () => (
    <aside
      className={`hidden lg:flex lg:flex-col ${showExpanded ? 'w-64' : 'w-16'} bg-slate-900 text-slate-300 fixed h-screen transition-all duration-300 ease-in-out z-20`}
      onMouseEnter={() => sidebarCollapsed && setSidebarHovered(true)}
      onMouseLeave={() => setSidebarHovered(false)}
      data-testid="sidebar"
    >
      <div className={`p-4 border-b border-slate-800 ${!showExpanded ? 'px-2' : ''}`}>
        <div className="flex items-center gap-3 justify-center">
          <img src="/logo.png" alt="ANT Broadband Logo" className={`${!showExpanded ? 'h-8' : 'h-12'} w-auto object-contain transition-all`} />
        </div>
        {showExpanded && <p className="text-xs text-slate-500 mt-2 pl-1">Order Management Portal</p>}
      </div>

      {showExpanded ? (
        <nav className="p-3 flex-1 overflow-y-auto">
          <GroupedNav />
        </nav>
      ) : (
        <CollapsedRail />
      )}

      <SidebarFooter collapsed={!showExpanded} />

      {!isWhatsAppPage && (
        <button
          onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
          className="absolute -right-3 top-20 w-6 h-6 bg-slate-700 rounded-full flex items-center justify-center text-white hover:bg-slate-600 transition-colors shadow-lg"
          data-testid="sidebar-collapse-toggle"
          title={sidebarCollapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {sidebarCollapsed ? <ChevronRight className="w-4 h-4" /> : <ChevronLeft className="w-4 h-4" />}
        </button>
      )}
    </aside>
  );

  const useTopbar = navMode === 'topbar';

  return (
    <div className="flex min-h-screen bg-slate-50" data-testid="app-layout">
      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-40 bg-slate-900 text-white h-16 flex items-center justify-between px-4 shadow-lg">
        <div className="flex items-center gap-3">
          <img src="/logo.png" alt="ANT" className="h-10 w-auto object-contain" />
          <span className="font-semibold text-sm">ANT Orders</span>
        </div>
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="p-2 rounded-lg hover:bg-slate-800 transition-colors"
          data-testid="mobile-menu-toggle"
          aria-label="Toggle menu"
        >
          {isMobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </header>

      {/* Mobile Menu Overlay */}
      {isMobileMenuOpen && (
        <div className="lg:hidden fixed inset-0 z-30 bg-black/50 backdrop-blur-sm" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      {/* Desktop nav: sidebar OR topbar */}
      {useTopbar ? <TopBar /> : <Sidebar />}

      {/* Mobile Drawer */}
      <aside
        className={`lg:hidden fixed top-16 left-0 bottom-16 z-40 w-72 bg-slate-900 text-slate-300 transform transition-transform duration-300 ease-in-out flex flex-col ${
          isMobileMenuOpen ? 'translate-x-0' : '-translate-x-full'
        }`}
        data-testid="mobile-sidebar"
      >
        <div className="p-3 border-b border-slate-800">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
            <Input
              value={mobileSearch}
              onChange={(e) => setMobileSearch(e.target.value)}
              placeholder="Search menu..."
              className="pl-9 h-9 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus-visible:ring-red-500"
              data-testid="mobile-menu-search"
            />
          </div>
        </div>
        <nav className="p-3 flex-1 overflow-y-auto">
          <GroupedNav onNavigate={() => setIsMobileMenuOpen(false)} searchTerm={mobileSearch} />
        </nav>
        <div className="p-3 border-t border-slate-800">
          <div className="mb-2 px-1 flex items-center justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.full_name}</p>
              <p className="text-xs text-slate-400 truncate">{user?.email}</p>
            </div>
          </div>
          <Button
            onClick={logout}
            variant="outline"
            className="w-full border-slate-700 text-slate-300 hover:bg-red-600 hover:text-white hover:border-red-600"
            data-testid="mobile-logout-button"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </aside>

      {/* Main Content */}
      <main className={`flex-1 transition-all duration-300 ${useTopbar ? '' : showExpanded ? 'lg:ml-64' : 'lg:ml-16'}`}>
        <div className={`pt-16 ${useTopbar ? 'lg:pt-16' : 'lg:pt-0'}`}>
          <div className={`${isWhatsAppPage ? 'p-0' : 'p-4 lg:p-8'}`}>
            {children}
          </div>
        </div>
      </main>

      {/* Mobile Bottom Navigation - Quick Access */}
      <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 bg-slate-900 border-t border-slate-800 safe-area-pb">
        <div className="flex justify-around items-center h-16">
          {flatItems.slice(0, 5).map((item) => {
            const Icon = item.icon;
            const active = isItemActive(item, location);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex flex-col items-center justify-center flex-1 h-full py-2 transition-colors ${
                  active ? 'text-red-500' : 'text-slate-400 active:text-white'
                }`}
                data-testid={`bottom-nav-${item.label.toLowerCase().replace(/ /g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-[10px] mt-1 font-medium">{item.label.split(' ')[0]}</span>
              </Link>
            );
          })}
        </div>
      </nav>

      <div className="lg:hidden h-16" />
    </div>
  );
}
